// src/App.tsx

import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedLayout } from './components/layout/ProtectedLayout';
import { PWAInstallPrompt } from './components/pwa/PWAInstallPrompt';

const loadLoginPage = () => import('./pages/auth/LoginPage');
const loadAutoRedirect = () => import('./pages/auth/AutoRedirect');
const loadAdminDashboard = () => import('./pages/admin/AdminDashboard');
const loadAppChangeControlPage = () => import('./pages/admin/AppChangeControlPage');
const loadWhatsAppNotificationsPage = () => import('./pages/admin/WhatsAppNotificationsPage');
const loadUserViewsManagementPage = () => import('./pages/admin/UserViewsManagementPage');
const loadQualityManagerDashboard = () => import('./pages/quality-dashboard/Dashboard');
const loadDocumentsPage = () => import('./pages/quality-manager/DocumentsPage');
const loadQualityApprovalsPage = () => import('./pages/quality-manager/QualityApprovalsPage');
const loadAuditorDashboard = () => import('./pages/auditor/AuditorDashboard');
const loadAuditPage = () => import('./pages/auditor/AuditPage');
const loadCreatorDashboard = () => import('./pages/creator/CreatorDashboard');
const loadReaderDashboard = () => import('./pages/reader/ReaderDashboard');
const loadNotFoundPage = () => import('./pages/shared/NotFoundPage');
const loadHomePage = () => import('./pages/HomePage');
const loadAuditoriaInterna = () => import('./pages/auditoria/AuditoriaInterna');
const loadAuditoriaProveedorCliente = () => import('./pages/auditoria/AuditoriaProveedorCliente');
const loadAuditoriaCertificacion = () => import('./pages/auditoria/AuditoriaCertificacion');
const loadMonitoreoIndicadores = () => import('./pages/revision-gerencial/MonitoreoIndicadores');
const loadAccionesGerencial = () => import('./pages/revision-gerencial/AccionesGerencial');
const loadMinutaGerencial = () => import('./pages/revision-gerencial/MinutaGerencial');
const loadCertificadoIATF = () => import('./pages/certificado/CertificadoIATF');
const loadMatrizRiesgos = () => import('./pages/analisis-riesgos/MatrizRiesgos');
const loadTratamientoRiesgo = () => import('./pages/analisis-riesgos/TratamientoRiesgo');
const loadMonitoreoRevision = () => import('./pages/analisis-riesgos/MonitoreoRevision');
const loadProgramaContingencia = () => import('./pages/plan-contingencia/ProgramaContingencia');
const loadSimulaciones = () => import('./pages/plan-contingencia/Simulaciones');
const loadResultadosContingencia = () => import('./pages/plan-contingencia/ResultadosContingencia');
const loadEncuestasSatisfaccion = () => import('./pages/requisitos-clientes/EncuestasSatisfaccion');
const loadConcentradoRequerimientos = () => import('./pages/requisitos-clientes/ConcentradoRequerimientos');
const loadDifusion = () => import('./pages/requisitos-clientes/Difusion');
const loadProcesosMejora = () => import('./pages/mejoras/ProcesosMejora');
const loadDocumentacionMejora = () => import('./pages/mejoras/DocumentacionMejora');
const loadPersonalMejora = () => import('./pages/mejoras/PersonalMejora');
const loadIndicadoresMejora = () => import('./pages/mejoras/IndicadoresMejora');
const loadClienteMejora = () => import('./pages/mejoras/ClienteMejora');
const loadMaterialDidactico = () => import('./pages/material-didactico/MaterialDidactico');
const loadAppSettingsPage = () => import('./pages/admin/settings/AppSettingsPage');

const LoginPage = lazy(loadLoginPage);
const AutoRedirect = lazy(loadAutoRedirect);
const AdminDashboard = lazy(loadAdminDashboard);
const AppChangeControlPage = lazy(loadAppChangeControlPage);
const WhatsAppNotificationsPage = lazy(loadWhatsAppNotificationsPage);
const UserViewsManagementPage = lazy(loadUserViewsManagementPage);
const QualityManagerDashboard = lazy(loadQualityManagerDashboard);
const DocumentsPage = lazy(loadDocumentsPage);
const QualityApprovalsPage = lazy(loadQualityApprovalsPage);
const AuditorDashboard = lazy(loadAuditorDashboard);
const AuditPage = lazy(loadAuditPage);
const CreatorDashboard = lazy(loadCreatorDashboard);
const ReaderDashboard = lazy(loadReaderDashboard);
const NotFoundPage = lazy(loadNotFoundPage);
const HomePage = lazy(loadHomePage);
const AuditoriaInterna = lazy(loadAuditoriaInterna);
const AuditoriaProveedorCliente = lazy(loadAuditoriaProveedorCliente);
const AuditoriaCertificacion = lazy(loadAuditoriaCertificacion);
const MonitoreoIndicadores = lazy(loadMonitoreoIndicadores);
const AccionesGerencial = lazy(loadAccionesGerencial);
const MinutaGerencial = lazy(loadMinutaGerencial);
const CertificadoIATF = lazy(loadCertificadoIATF);
const MatrizRiesgos = lazy(loadMatrizRiesgos);
const TratamientoRiesgo = lazy(loadTratamientoRiesgo);
const MonitoreoRevision = lazy(loadMonitoreoRevision);
const ProgramaContingencia = lazy(loadProgramaContingencia);
const Simulaciones = lazy(loadSimulaciones);
const ResultadosContingencia = lazy(loadResultadosContingencia);
const EncuestasSatisfaccion = lazy(loadEncuestasSatisfaccion);
const ConcentradoRequerimientos = lazy(loadConcentradoRequerimientos);
const Difusion = lazy(loadDifusion);
const ProcesosMejora = lazy(loadProcesosMejora);
const DocumentacionMejora = lazy(loadDocumentacionMejora);
const PersonalMejora = lazy(loadPersonalMejora);
const IndicadoresMejora = lazy(loadIndicadoresMejora);
const ClienteMejora = lazy(loadClienteMejora);
const MaterialDidactico = lazy(loadMaterialDidactico);
const AppSettingsPage = lazy(loadAppSettingsPage);

const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 flex items-start justify-center pt-10">
    <div className="h-1.5 w-44 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full w-1/2 bg-gradient-to-r from-blue-400/70 to-cyan-300/70 animate-pulse" />
    </div>
  </div>
);

function App() {
  useEffect(() => {
    // Precarga silenciosa de vistas para evitar el "Cargando..." al primer cambio de ruta.
    const timer = window.setTimeout(() => {
      void Promise.allSettled([
        loadHomePage(),
        loadAdminDashboard(),
        loadAppChangeControlPage(),
        loadWhatsAppNotificationsPage(),
        loadUserViewsManagementPage(),
        loadQualityManagerDashboard(),
        loadDocumentsPage(),
        loadQualityApprovalsPage(),
        loadAuditorDashboard(),
        loadAuditPage(),
        loadCreatorDashboard(),
        loadReaderDashboard(),
        loadNotFoundPage(),
        loadAutoRedirect(),
        loadLoginPage(),
        loadAuditoriaInterna(),
        loadAuditoriaProveedorCliente(),
        loadAuditoriaCertificacion(),
        loadMonitoreoIndicadores(),
        loadAccionesGerencial(),
        loadMinutaGerencial(),
        loadCertificadoIATF(),
        loadMatrizRiesgos(), loadTratamientoRiesgo(), loadMonitoreoRevision(),
        loadProgramaContingencia(), loadSimulaciones(), loadResultadosContingencia(),
        loadEncuestasSatisfaccion(), loadConcentradoRequerimientos(), loadDifusion(),
        loadProcesosMejora(), loadDocumentacionMejora(), loadPersonalMejora(),
        loadIndicadoresMejora(), loadClienteMejora(), loadMaterialDidactico(),
        loadAppSettingsPage(),
      ]);
    }, 250);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SidebarProvider>
          <Router>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Rutas públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auto-redirect" element={<AutoRedirect />} />

          {/* Rutas de Admin */}
          <Route path="/admin/dashboard" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AdminDashboard /></ProtectedLayout>} />
          <Route path="/admin/app-change-control" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AppChangeControlPage /></ProtectedLayout>} />
          <Route path="/admin/whatsapp" element={<ProtectedLayout requiredRole="Administrador_Calidad"><WhatsAppNotificationsPage /></ProtectedLayout>} />
          <Route path="/admin/user-views" element={<ProtectedLayout requiredRole="Administrador_Calidad"><UserViewsManagementPage /></ProtectedLayout>} />

          {/* Rutas de Quality Manager (admin también accede) */}
          <Route path="/quality/dashboard" element={<ProtectedLayout requiredRole={['Responsable_Calidad', 'Administrador_Calidad']}><QualityManagerDashboard /></ProtectedLayout>} />
          <Route path="/quality/documents" element={<ProtectedLayout requiredRole={['Responsable_Calidad', 'Administrador_Calidad']}><DocumentsPage /></ProtectedLayout>} />
          <Route path="/quality/approvals" element={<ProtectedLayout requiredRole={['Responsable_Calidad', 'Administrador_Calidad']}><QualityApprovalsPage /></ProtectedLayout>} />

          {/* Rutas de Approver (admin también accede) */}
          <Route path="/approver/dashboard" element={<Navigate to="/quality/approvals" replace />} />
          <Route path="/approvals" element={<ProtectedLayout requiredRole={['Aprobador', 'Responsable_Calidad', 'Revisor']}><QualityApprovalsPage /></ProtectedLayout>} />

          {/* Rutas de Auditor (admin también accede) */}
          <Route path="/audit/dashboard" element={<ProtectedLayout requiredRole="Auditor_Interno"><AuditorDashboard /></ProtectedLayout>} />
          <Route path="/audit/history" element={<ProtectedLayout requiredRole="Auditor_Interno"><AuditPage /></ProtectedLayout>} />

          {/* Rutas de Creator (Jefe) */}
          <Route path="/creator/dashboard" element={<ProtectedLayout requiredRole="Creador_Documentos"><CreatorDashboard /></ProtectedLayout>} />

          {/* Rutas de Reader (Visitante/Lector) */}
          <Route path="/documents/view" element={<ProtectedLayout requiredRole={['Lector', 'Creador_Documentos', 'Auditor_Interno', 'Aprobador', 'Revisor']}><ReaderDashboard /></ProtectedLayout>} />

          {/* Rutas legacy (compatibilidad) */}
          <Route path="/dashboard" element={<Navigate to="/quality/dashboard" replace />} />
          <Route path="/documents" element={<Navigate to="/quality/documents" replace />} />
          <Route path="/audit" element={<Navigate to="/audit/history" replace />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

          {/* Auditorías */}
          <Route path="/auditoria/interna" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AuditoriaInterna /></ProtectedLayout>} />
          <Route path="/auditoria/proveedor-cliente" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AuditoriaProveedorCliente /></ProtectedLayout>} />
          <Route path="/auditoria/certificacion" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AuditoriaCertificacion /></ProtectedLayout>} />

          {/* Revisión Gerencial */}
          <Route path="/revision-gerencial/monitoreo" element={<ProtectedLayout requiredRole="Administrador_Calidad"><MonitoreoIndicadores /></ProtectedLayout>} />
          <Route path="/revision-gerencial/acciones" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AccionesGerencial /></ProtectedLayout>} />
          <Route path="/revision-gerencial/minuta" element={<ProtectedLayout requiredRole="Administrador_Calidad"><MinutaGerencial /></ProtectedLayout>} />

          {/* Certificado */}
          <Route path="/certificado/iatf" element={<ProtectedLayout requiredRole="Administrador_Calidad"><CertificadoIATF /></ProtectedLayout>} />

          {/* Análisis de Riesgos */}
          <Route path="/analisis-riesgos/matriz" element={<ProtectedLayout requiredRole="Administrador_Calidad"><MatrizRiesgos /></ProtectedLayout>} />
          <Route path="/analisis-riesgos/tratamiento" element={<ProtectedLayout requiredRole="Administrador_Calidad"><TratamientoRiesgo /></ProtectedLayout>} />
          <Route path="/analisis-riesgos/monitoreo" element={<ProtectedLayout requiredRole="Administrador_Calidad"><MonitoreoRevision /></ProtectedLayout>} />

          {/* Plan de Contingencia */}
          <Route path="/plan-contingencia/programa" element={<ProtectedLayout requiredRole="Administrador_Calidad"><ProgramaContingencia /></ProtectedLayout>} />
          <Route path="/plan-contingencia/simulaciones" element={<ProtectedLayout requiredRole="Administrador_Calidad"><Simulaciones /></ProtectedLayout>} />
          <Route path="/plan-contingencia/resultados" element={<ProtectedLayout requiredRole="Administrador_Calidad"><ResultadosContingencia /></ProtectedLayout>} />

          {/* Requisitos Específicos de Clientes */}
          <Route path="/requisitos-clientes/encuestas" element={<ProtectedLayout requiredRole="Administrador_Calidad"><EncuestasSatisfaccion /></ProtectedLayout>} />
          <Route path="/requisitos-clientes/concentrado" element={<ProtectedLayout requiredRole="Administrador_Calidad"><ConcentradoRequerimientos /></ProtectedLayout>} />
          <Route path="/requisitos-clientes/difusion" element={<ProtectedLayout requiredRole="Administrador_Calidad"><Difusion /></ProtectedLayout>} />

          {/* Mejoras */}
          <Route path="/mejoras/procesos" element={<ProtectedLayout requiredRole="Administrador_Calidad"><ProcesosMejora /></ProtectedLayout>} />
          <Route path="/mejoras/documentacion" element={<ProtectedLayout requiredRole="Administrador_Calidad"><DocumentacionMejora /></ProtectedLayout>} />
          <Route path="/mejoras/personal" element={<ProtectedLayout requiredRole="Administrador_Calidad"><PersonalMejora /></ProtectedLayout>} />
          <Route path="/mejoras/indicadores" element={<ProtectedLayout requiredRole="Administrador_Calidad"><IndicadoresMejora /></ProtectedLayout>} />
          <Route path="/mejoras/cliente" element={<ProtectedLayout requiredRole="Administrador_Calidad"><ClienteMejora /></ProtectedLayout>} />

          {/* Material Didáctico */}
          <Route path="/material-didactico" element={<ProtectedLayout requiredRole="Administrador_Calidad"><MaterialDidactico /></ProtectedLayout>} />

          {/* Configuración */}
          <Route path="/admin/settings" element={<ProtectedLayout requiredRole="Administrador_Calidad"><AppSettingsPage /></ProtectedLayout>} />

          {/* Home */}
          <Route path="/" element={<ProtectedLayout><HomePage /></ProtectedLayout>} />

          {/* Not Found */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
          </Suspense>
        </Router>
      </SidebarProvider>
      </AuthProvider>
      <PWAInstallPrompt />
    </ThemeProvider>
  );
}

export default App;
